// Scenario mapping: missionId -> scenario data
// Used by ChapterDetailScreen to load the correct story content

import type { ScenarioChapter, SequenceItem } from './storyLoader';
import { getRuntimeScenario } from './scenarioStore';

export type { ScenarioChapter, SequenceItem };
export { loadRuntimeScenario, getRuntimeScenario, getRuntimeCharacter } from './scenarioStore';

export interface ScenarioMeta {
  testMode?: boolean;
  id: string;
  missionId: number;
  title: string;
  certification: string;
  chapters: ScenarioChapter[];
  /** Personnages du scénario servi par le runtime (/me/stories/{id}/scenario) : chacun porte
   *  une `avatarUrl` publique. Absent des scénarios bundlés (avatars locaux par storyKey). */
  characters?: { id: string; name: string; role?: string; color?: string; avatarUrl?: string | null }[];
}

export function getScenarioForMission(missionId: number): ScenarioMeta | undefined {
  // SOURCE UNIQUE : LA BASE (CCT-253). Il y avait ici un repli sur le scénario BUNDLÉ quand
  // le runtime n'avait rien. Ce repli est la cause commune de presque tous les symptômes
  // rencontrés : un 404 sur `/me/stories/{id}/scenario` passait inaperçu parce que l'écran
  // affichait l'histoire embarquée à la place ; la tâche ouverte n'avait aucune user story
  // côté moteur, puisque le serveur ne connaissait pas l'élément ; le statut disparaissait
  // sans explication ; et la progression n'était suivie nulle part.
  //
  // L'app montrait donc du contenu que le serveur n'avait jamais vu, sans le dire. Mieux vaut
  // un écran vide QUI SE VOIT qu'une histoire crédible qui ne mène à rien.
  const runtime = getRuntimeScenario(missionId);
  if (runtime) {
    return {
      id: runtime.id,
      testMode: runtime.testMode,
      missionId,
      title: runtime.title,
      certification: runtime.certification,
      chapters: runtime.chapters,
      // Les personnages viennent de la MÊME source que les chapters : sans eux, les écrans
      // retombaient sur le bundle local (avatars/équipe d'une autre histoire).
      characters: (runtime as any).characters,
      // MUSIQUE de la mission (CCT-130) : servie à la RACINE du scénario. Omise ici, l'app
      // retombait sur la piste bundlée — donc la musique d'une autre histoire, alors que
      // l'auteur en avait choisi une. Les pistes de chapitre/élément voyagent, elles, dans
      // `chapters` (transmis tel quel).
      music: (runtime as any).music,
    } as any;
  }
  // Rien en base : on ne fabrique RIEN. L'appelant affiche un état vide explicite.
  return undefined;
}

/**
 * LE personnage joué par l'apprenant dans cette mission (CCT-148/151), désigné dans le Story
 * Builder (`is_player`). Renvoie `undefined` si l'auteur n'en a désigné aucun — l'app retombe
 * alors sur l'identité de PROFIL (nom + avatar choisis par le joueur).
 *
 * Source UNIQUE : sans elle, chaque écran refaisait sa propre recherche et le joueur portait un
 * nom ici, un autre là — c'est exactement ce qu'on corrige.
 */
export function getMissionPlayer(missionId: number): {
  name: string; role: string; avatarUrl?: string | null; characterId?: number | null;
} | undefined {
  const chars: any[] = (getScenarioForMission(missionId) as any)?.characters ?? [];
  const p = chars.find((c) => c?.isPlayer);
  return p ? { name: p.name, role: p.role ?? '', avatarUrl: p.avatarUrl ?? null,
               characterId: p.characterId ?? null } : undefined;
}
